import { create } from "zustand";

/**
 * Single, minimal Zustand store for stop-at-5.00s (hard rule: one store per app).
 *
 *   idle → running (timer visible to 2.00s, then blind) → [tap] → done
 *
 * The brag number is the miss distance: |stopped − 5000| in ms, lower is better.
 * Timing uses performance.now() (monotonic). The rAF ticker lives in the component;
 * the store stays pure/testable.
 */

export const TARGET_MS = 5_000;
/** Timer is visible this long, then goes blind — count the rest in your head. */
export const BLIND_AFTER_MS = 2_000;
/** Safety cap: a run left hanging auto-stops here (delta maxes at exactly 5000,
 *  the validate.ts ceiling). */
export const CAP_MS = 10_000;

export type Phase = "idle" | "running" | "done";

interface StopState {
  phase: Phase;
  /** performance.now() at start; null outside a run. */
  startedAt: number | null;
  /** Elapsed ms when stopped (rounded); null until done. */
  stoppedMs: number | null;
  /** |stoppedMs − TARGET_MS| — the brag number. null until done. */
  delta: number | null;

  /** All-time best delta before this run, from localStorage. null if first ever. */
  prevBest: number | null;
  /** True once a finished run beats prevBest — drives the "NEW BEST" celebration. */
  isNewBest: boolean;

  start: (at: number) => void;
  /** Player tapped stop at `at` (performance.now()). Caps at CAP_MS. */
  stop: (at: number) => void;
  reset: () => void;
}

const PB_KEY = "kp:stop:best";

function loadPrevBest(): number | null {
  try {
    const v = localStorage.getItem(PB_KEY);
    return v === null ? null : Number.parseInt(v, 10);
  } catch {
    return null;
  }
}

function savePrevBest(delta: number): void {
  try {
    localStorage.setItem(PB_KEY, String(delta));
  } catch {
    /* storage blocked — fine */
  }
}

export const useStopStore = create<StopState>((set, get) => ({
  phase: "idle",
  startedAt: null,
  stoppedMs: null,
  delta: null,
  prevBest: loadPrevBest(),
  isNewBest: false,

  start: (at) => set({ phase: "running", startedAt: at, stoppedMs: null, delta: null }),

  stop: (at) => {
    const { phase, startedAt, prevBest } = get();
    if (phase !== "running" || startedAt === null) return; // ignore stray taps
    const stoppedMs = Math.min(Math.round(at - startedAt), CAP_MS);
    const delta = Math.abs(stoppedMs - TARGET_MS);

    const beat = prevBest === null || delta < prevBest; // lower is better
    if (beat) savePrevBest(delta);
    set({ phase: "done", startedAt: null, stoppedMs, delta, isNewBest: beat });
  },

  // Reload prevBest (it may have just advanced) and clear the celebration flag.
  reset: () =>
    set({
      phase: "idle",
      startedAt: null,
      stoppedMs: null,
      delta: null,
      prevBest: loadPrevBest(),
      isNewBest: false,
    }),
}));

/** "5.003s" formatting for the stopped clock on the result screen. */
export function formatStopped(ms: number): string {
  return `${(ms / 1000).toFixed(3)}s`;
}
